import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
  MenuItem,
  Select,
  InputLabel,
  Box,
  FormControl,
} from '@material-ui/core';
import { ChevronDown } from '@eduzz/houston-icons';
import { useState, useEffect } from 'react';
import { IQuestion, getQuestions, getCourseOptions, saveMessage } from 'services/help';
import { useSelector } from 'react-redux';
import { IUser, enUserType } from 'interface/user';
import FieldText from '@eduzz/houston-ui/Forms/Text';
import Button from '@eduzz/houston-ui/Button';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ICourse } from 'interface/course';
import CenteredPage from 'components/Shared/CenteredPage';

const useStyles = makeStyles((theme) => ({
  root: {
    '& *::placeholder': {
      color: '#97b4c2 !important',
    },
  },
  page: {
    width: '100%',
    minHeight: '100vh',
    background: theme.palette.neutrals.black,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    width: '100%',
    '& form': {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      maxWidth: 440,
    },
  },
  title: {
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  textButton: {
    marginTop: theme.spacing(4),
    fontWeight: 700,
    cursor: 'pointer',
    color: theme.palette.grey[600],
    '& span': {
      color: theme.palette.info.light,
    },
  },
  subtitle: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(6),
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  question: {
    color: theme.palette.secondary.light,
  },
  answer: {
    color: theme.palette.common.white,
  },
  summary: {
    background: theme.palette.neutrals.black,
    borderTop: `1px solid ${theme.palette.grey[600]}`,
  },
  details: {
    background: theme.palette.neutrals.black,
  },
  accordion: {
    boxShadow: 'none',
    '& .houston-icon': {
      color: theme.palette.primary.light,
    },
  },
  input: {
    '& label': {
      fontWeight: 600,
      color: `${theme.palette.grey[500]} !important`,
    },
    '& Mui-focused': {
      color: `${theme.palette.grey[500]} !important`,
    },
    '& fieldset': {
      borderColor: `${theme.palette.grey[300]} !important`,
    },
    '& input, textarea': {
      color: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
    },
    '& .Mui-disabled': {
      background: 'none',
      color: theme.palette.grey[300],
    },
    '& .houston-icon': {
      color: theme.palette.common.white,
    },
  },
  name: {
    color: theme.palette.common.white,
    margin: theme.spacing(0, 2),
    marginRight: 0,
    maxWidth: 300,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  selectRoot: {
    marginRight: theme.spacing(-4),
    '& > div': {
      '&:after, &:before': {
        content: 'none !important',
      },
    },
  },
  selectMenu: {
    border: 'none !important',
    paddingRight: 0,
    paddingLeft: theme.spacing(6),
    fontSize: 14,
  },
  selectValue: {
    color: theme.palette.common.white,
    paddingTop: 13,
    paddingBottom: 13,
    paddingLeft: 13,
  },
  selectPlaceholder: {
    color: '#4e5a64',
  },
  selectLabel: {
    fontSize: 14,
  },
  dropdownMenu: {
    background: theme.palette.dark[600],
    overflowY: 'scroll',
    '& li': {
      color: theme.palette.common.white,
    },
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const validationSchema = yup.object({
  name: yup.string().required('O campo nome é obrigatório'),
  email: yup.string().required('O campo email é obrigatório'),
  title: yup
    .string()
    .required('O campo título é obrigatório')
    .min(3, 'O campo título precisa ter no mínimo 3 caracteres'),
  message: yup.string().required('O campo descrição é obrigatório'),
  cou_hash: yup.string().min(2, 'O campo de curso é obrigatório'),
});

const Help = () => {
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [courses, setCourses] = useState<ICourse[]>([]);
  const user = useSelector((state: { user: IUser }) => state.user);
  const classes = useStyles();
  const formik = useFormik({
    initialValues: {
      name: user.name ?? '',
      email: user.email ?? '',
      title: '',
      message: '',
      cou_hash: ' ',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      saveMessage(values).finally(() => {
        formik.setSubmitting(false);
      });
    },
  });

  const openHelpLink = () => {
    window.open('https://ajuda.eduzz.com/?article-category=nutror', '_blank');
  };

  useEffect(() => {
    if (user?.id) {
      getQuestions()
        .then((questions) => {
          if (user.type === enUserType.learner) {
            return questions.learner;
          }
          return questions.producer;
        })
        .then(setQuestions);
    }
  }, [user]);

  useEffect(() => {
    getCourseOptions().then(setCourses);
  }, []);

  const hasUser = Boolean(user.name) && Boolean(user.email);

  return (
    <CenteredPage marginTop={12}>
      <div className={classes.root}>
        <div className={classes.page}>
          <div className={classes.container}>
            <Typography className={classes.title} variant='body1'>
              Ficou com alguma dúvida sobre o curso ou a plataforma?
            </Typography>
            <Typography className={classes.subtitle} variant='body1'>
              Dúvidas Frequentes
            </Typography>
            {(questions ?? []).map(({ question, answer }, key) => (
              <Accordion key={key} classes={{ root: classes.accordion }}>
                <AccordionSummary className={classes.summary} expandIcon={<ChevronDown />}>
                  <Typography
                    className={classes.question}
                    variant='body1'
                    component='div'
                    dangerouslySetInnerHTML={{ __html: question }}
                  />
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Typography
                    className={classes.answer}
                    variant='body1'
                    component='div'
                    dangerouslySetInnerHTML={{ __html: answer }}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
            <Typography className={classes.subtitle} variant='body1'>
              Solicitação de ajuda
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              {!hasUser && (
                <>
                  <FieldText
                    fullWidth
                    type='text'
                    name='name'
                    value={formik.values.name}
                    className={classes.input}
                    errorMessage={formik.errors.name}
                    placeholder='Escreva aqui o título da sua solicitação'
                    disabled={formik.isSubmitting}
                    label='Nome'
                    onChange={formik.handleChange('name')}
                  />
                  <FieldText
                    fullWidth
                    type='text'
                    name='email'
                    value={formik.values.email}
                    className={classes.input}
                    errorMessage={formik.errors.email}
                    placeholder='Escreva aqui o título da sua solicitação'
                    disabled={formik.isSubmitting}
                    label='email'
                    onChange={formik.handleChange('email')}
                  />
                </>
              )}
              <FieldText
                fullWidth
                type='text'
                name='title'
                value={formik.values.title}
                className={classes.input}
                errorMessage={formik.errors.title}
                placeholder='Escreva aqui o título da sua solicitação'
                disabled={formik.isSubmitting}
                label='Título da solicitação'
                onChange={formik.handleChange('title')}
              />
              <Box marginTop={1} marginBottom={4} width='100%'>
                <FormControl className={classes.input} fullWidth variant='outlined'>
                  <InputLabel shrink={true} id='help-select-course' className={classes.selectLabel}>
                    Selecionar curso
                  </InputLabel>
                  <Select
                    labelId='help-select-course'
                    placeholder='Selecione seu curso'
                    name='cou_hash'
                    labelWidth={115}
                    classes={{
                      select: [classes.selectValue, formik.values.cou_hash == ' ' && classes.selectPlaceholder].join(
                        ' '
                      ),
                      selectMenu: classes.selectMenu,
                    }}
                    value={formik.values.cou_hash}
                    MenuProps={{
                      classes: { paper: classes.dropdownMenu },
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                    }}
                    onChange={formik.handleChange}
                    IconComponent={(props) => <ChevronDown {...props} />}
                  >
                    <MenuItem value=' ' disabled>
                      Selecione seu curso
                    </MenuItem>
                    {courses.map((option) => (
                      <MenuItem value={option.hash}>{option.title}</MenuItem>
                    ))}
                  </Select>
                  {formik.errors.cou_hash && (
                    <Typography variant='caption' className={classes.error}>
                      {formik.errors.cou_hash}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              <FieldText
                fullWidth
                type='text'
                value={formik.values.message}
                name='message'
                className={classes.input}
                errorMessage={formik.errors.message}
                placeholder='Escreva aqui a descrição da solicitação'
                disabled={formik.isSubmitting}
                multiline={true}
                label='Descrição da solicitação'
                onChange={formik.handleChange('message')}
              />
              <Button variant='contained' type='submit' loading={formik.isSubmitting}>
                Enviar solicitação
              </Button>

              <Typography className={classes.textButton} variant='body1' onClick={openHelpLink}>
                Não achou o que precisa? Acesse nossa <span>Central de Ajuda</span>
              </Typography>
            </form>
          </div>
        </div>
      </div>
    </CenteredPage>
  );
};

export default Help;
